import { Box, chakra, Flex, Tooltip } from '@chakra-ui/react';
import { Group } from '@visx/group';
import { withParentSize } from '@visx/responsive';
import {
  WithParentSizeProps,
  WithParentSizeProvidedProps,
} from '@visx/responsive/lib/enhancers/withParentSize';
import { scaleLinear } from '@visx/scale';
import { LineRadial } from '@visx/shape';
import { CSSProperties, useMemo } from 'react';

import { getStringWidth } from '../../utils/get-string-width';
import { useChartStructureColors } from '../status-tag/chart-colors';
import { ChartLegend } from './chart-legend';

declare global {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface Array<T> {
    fill<R>(value: R): R[];
  }
}

export interface RadarDatum {
  label: string;
  color: string;
  dimensions: number[];
}

export interface RadarChartProps {
  data: RadarDatum[];
  levels: number;
  dimentions: RadarDimention[];
  yRange?: [number, number];
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
  background?: string;
  radialLineColor?: string;
  labelZoneWidth?: number;
  labelStyles?: CSSProperties;
  legendMargin?: number;
  legendHeight?: number;
  legendStyles?: CSSProperties;
}

export interface RadarDimention {
  label: string;
}

export const RadarChart = withParentSize(RadarChartComponent) as unknown as (
  props: RadarChartProps,
) => JSX.Element;

export function RadarChartComponent(
  props: RadarChartProps & WithParentSizeProvidedProps & WithParentSizeProps,
) {
  const levels = useMemo(
    () => Math.max(0, Math.floor(toFiniteNumber(props.levels))),
    [props.levels],
  );
  const legendAreaHeight = toFiniteNumber(props.legendHeight, 44);
  const width = Math.max(0, toFiniteNumber(props.parentWidth));
  const parentHeight = Math.max(0, toFiniteNumber(props.parentHeight));
  const height = Math.max(0, parentHeight - legendAreaHeight);
  const {
    top: rawMarginTop = 0,
    right: rawMarginRight = 0,
    bottom: rawMarginBottom = 0,
    left: rawMarginLeft = 0,
  } = props.margin ?? {};
  const marginTop = toFiniteNumber(rawMarginTop);
  const marginRight = toFiniteNumber(rawMarginRight);
  const marginBottom = toFiniteNumber(rawMarginBottom);
  const marginLeft = toFiniteNumber(rawMarginLeft);
  const background = props.background ?? 'transparent';
  const structure = useChartStructureColors();
  /* The guide lines used to hardcode a light gray that dark mode never
   * overrode; both now read the neutral grid step. */
  const radialLineColor = props.radialLineColor ?? structure.grid;
  const labelColor = structure.axisLabel;

  const xMax = useMemo(
    () => Math.max(0, width - marginLeft - marginRight),
    [width, marginLeft, marginRight],
  );
  const yMax = useMemo(
    () => Math.max(0, height - marginTop - marginBottom),
    [height, marginTop, marginBottom],
  );
  const labelOffset = 24;
  const labelHeight = 20;
  const minHorizontalLabelZone = toFiniteNumber(props.labelZoneWidth, 40);
  const minVerticalLabelZone = labelOffset + labelHeight / 2;
  const maxDiameter = 350;
  const dimentionsCount = useMemo(() => props.dimentions.length, [props.dimentions]);
  const sanitizedData = useMemo(
    () =>
      props.data.map((datum) => ({
        ...datum,
        dimensions: sanitizeDimensions(datum.dimensions, dimentionsCount),
      })),
    [dimentionsCount, props.data],
  );

  const chartWidth = useMemo(
    () => xMax - 2 * minHorizontalLabelZone,
    [xMax, minHorizontalLabelZone],
  );
  const chartHeight = useMemo(() => yMax - 2 * minVerticalLabelZone, [yMax, minVerticalLabelZone]);
  const calculatedDiameter = useMemo(
    () => Math.max(0, Math.min(chartWidth, chartHeight)),
    [chartWidth, chartHeight],
  );
  const radius = useMemo(() => Math.min(maxDiameter, calculatedDiameter) / 2, [calculatedDiameter]);
  const canRenderChart = useMemo(
    () => width > 0 && height > 0 && Number.isFinite(radius) && radius > 0 && dimentionsCount > 0,
    [dimentionsCount, height, radius, width],
  );

  const webs = useMemo(() => genAngles(dimentionsCount), [dimentionsCount]);

  const radialLines = useMemo(
    () =>
      new Array(levels)
        .fill(null)
        .map((_, i) => (
          <LineRadial
            key={`radial-line-${i}`}
            data={webs}
            angle={radialScale}
            radius={levels > 0 ? ((i + 1) * radius) / levels : 0}
            fill="none"
            stroke={radialLineColor}
            strokeWidth={1}
            strokeLinecap="round"
          />
        )),
    [levels, webs, radius, radialLineColor],
  );

  const yScale = useMemo(() => {
    const maxValue = Math.max(0, ...sanitizedData.flatMap((d) => d.dimensions));

    return scaleLinear<number>({
      range: [0, radius],
      domain: sanitizeYRange(props.yRange, maxValue),
    });
  }, [radius, props.yRange, sanitizedData]);
  const polygonData = useMemo(
    () =>
      sanitizedData.map((datum) => ({
        ...datum,
        pointString: genPolygonPoints(datum.dimensions, yScale),
      })),
    [sanitizedData, yScale],
  );

  const polygons = useMemo(
    () =>
      polygonData.map((polygon) => (
        <polygon
          key={`polygon-${polygon.label}`}
          points={polygon.pointString}
          fill={polygon.color}
          fillOpacity={0.15}
          stroke={polygon.color}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )),
    [polygonData],
  );

  const labelPoints = useMemo(
    () => genPoints(dimentionsCount, radius + labelOffset),
    [dimentionsCount, labelOffset, radius],
  );

  const labelFontSize = 14;
  const labelFontWeight = 500;

  const labels = useMemo(
    () =>
      labelPoints.map((point, i) => {
        const label = props.dimentions[i]?.label ?? '';
        const isRight = point.x > 0.01;
        const isLeft = point.x < -0.01;

        const textWidth = getStringWidth(label, null, labelFontSize, labelFontWeight);
        const availableWidth = Math.max(0, width / 2 - Math.abs(point.x));
        const isTruncated = textWidth > availableWidth;
        const labelWidth = Math.min(availableWidth, textWidth);

        if (labelWidth < 1) {
          return null;
        }

        const x = isRight ? point.x : isLeft ? point.x - labelWidth : point.x - labelWidth / 2;
        const y = point.y - labelHeight / 2;

        return (
          <foreignObject
            key={`label-${label || i}`}
            x={x}
            y={y}
            width={labelWidth}
            height={labelHeight}
          >
            <Tooltip
              label={label}
              hasArrow
              placement="top-start"
              openDelay={300}
              isDisabled={!isTruncated}
            >
              <chakra.span
                display="block"
                fontSize={`${labelFontSize}px`}
                fontWeight={labelFontWeight}
                color={labelColor}
                maxW={`${labelWidth}px`}
                lineHeight={`${labelHeight}px`}
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {label}
              </chakra.span>
            </Tooltip>
          </foreignObject>
        );
      }),
    [labelColor, labelPoints, props.dimentions, labelFontSize, labelFontWeight, labelHeight, width],
  );

  const legendItems = useMemo(
    () =>
      props.data.map((datum) => ({
        key: datum.label,
        label: datum.label,
        color: datum.color,
      })),
    [props.data],
  );

  return (
    <Flex direction="column" w={width} h={parentHeight}>
      <ChartLegend items={legendItems} mb={6} flexShrink={0} />
      <Box flex={1} minH={0} w="full">
        <svg width={width} height={height}>
          <rect fill={background} width={width} height={height} rx={14} />
          <Group top={height / 2} left={width / 2}>
            <rect
              x={-width / 2}
              y={-height / 2}
              width={width}
              height={height}
              fill="transparent"
              pointerEvents="none"
            />
            {canRenderChart ? radialLines : null}
            {canRenderChart ? polygons : null}
            {canRenderChart ? labels : null}
          </Group>
        </svg>
      </Box>
    </Flex>
  );
}

// Rotate the radar chart by 180 degrees facing up
const angleOffset = Math.PI;

const radialScale = scaleLinear<number>({
  range: [0, Math.PI * 2],
  domain: [360, 0],
});

function genAngles(length: number) {
  if (length === 0) {
    return [];
  }

  const step = 360 / length;

  return new Array(length + 1).fill(null).map((_, i) => i * step);
}

function genPolygonPoints(data: number[], scale: (n: number) => number) {
  if (data.length === 0) {
    return '';
  }

  const step = (Math.PI * 2) / data.length;

  const points = data.flatMap((d, i) => {
    const scaledValue = scale(d);

    if (!Number.isFinite(scaledValue)) {
      return [];
    }

    return [
      `${scaledValue * Math.sin(i * step + angleOffset)},${
        scaledValue * Math.cos(i * step + angleOffset)
      }`,
    ];
  });

  return points.join(' ');
}

function genPoints(length: number, radius: number) {
  if (length === 0 || !Number.isFinite(radius)) {
    return [];
  }

  const step = (Math.PI * 2) / length;

  return new Array(length).fill(null).map((_, i) => ({
    x: radius * Math.sin(i * step + angleOffset),
    y: radius * Math.cos(i * step + angleOffset),
  }));
}

function sanitizeDimensions(dimensions: number[], maxLength: number): number[] {
  return dimensions.slice(0, maxLength).map((dimension) => Math.max(0, toFiniteNumber(dimension)));
}

function sanitizeYRange(yRange: [number, number] | undefined, maxValue: number): [number, number] {
  if (!yRange) {
    return [0, Math.max(1, maxValue)];
  }

  const minValue = toFiniteNumber(yRange[0]);
  const maxRangeValue = toFiniteNumber(yRange[1], Math.max(1, maxValue));

  if (maxRangeValue <= minValue) {
    return [0, Math.max(1, maxValue)];
  }

  return [minValue, maxRangeValue];
}

function toFiniteNumber(value: number | null | undefined, fallback = 0): number {
  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : fallback;
}
